import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { LocalStorageService } from 'app/core/services/localstorage.service';
import { ProfileSettingsService } from '../profile-settings/profile-settings.service';
import { HotToastService } from '@ngneat/hot-toast';
import moment from 'moment';
import { catchError, map, of, take } from 'rxjs';

@Component({
  selector: 'app-timezone-changed-popup',
  templateUrl: './timezone-changed-popup.component.html',
  styleUrl: './timezone-changed-popup.component.scss',
})
export class TimezoneChangedPopupComponent implements OnInit{
  userPreferredTimezone: any;
  currentUser: any;
  systemTimeZone: any;
  translationsLoaded: boolean = false;
  imgLoaded: boolean = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public dialogData: any, 
    private dialogRef: MatDialogRef<TimezoneChangedPopupComponent>,
    private profileSettingsService: ProfileSettingsService,
    private toastService: HotToastService,
    private translateService: TranslateService,
    private localStorageService: LocalStorageService){
  }

  ngOnInit(): void {
    this.systemTimeZone = this.normalizeTimezone(moment.tz.guess());
    this.userPreferredTimezone = this.localStorageService.getLocalStorage('user_preferences')?.timezone;
    this.currentUser = this.localStorageService.getLocalStorage('user');
    this.checkTranslationsLoaded();
  }

  checkTranslationsLoaded(retryCount = 0): void {
    const requiredKeys = [
      'text.timezone_changed',
      'text.timezone_changed.description',
      'text.timezone_changed.note',
      'cancel.text',
      'update'
    ];

    const MAX_RETRIES = 3;

    this.translateService.get(requiredKeys).pipe(
      take(1),
      map(translations => {
        return requiredKeys.every(key => 
          translations[key] && translations[key] !== key
        );
      }),
      catchError(() => {
        return of(false);
      })
    ).subscribe(allLoaded => {
      this.translationsLoaded = allLoaded;
      if (!allLoaded) {
        if(retryCount < MAX_RETRIES){
          setTimeout(() => this.checkTranslationsLoaded(retryCount + 1), 100);
        }else{
          const fallbacks = {
            'text.timezone_changed': 'Your timezone has changed',
            'text.timezone_changed.description': "The timezone setting for your computer is different from your account's timezone",
            'text.timezone_changed.note': 'This may cause date & time fields to display incorrect values. Would you like to update your account timezone in Zuper to your system timezone?',
            'cancel.text': 'Cancel',
            'update': 'Update'
          };
          for (const key of requiredKeys) {
            this.translateService.set(key, fallbacks[key]);
          }
          this.translationsLoaded = true;
        }
      }
    });

  }


  closeDialog(): void{
    this.dialogRef.close();
  }

  normalizeTimezone(timezone: string): string{
    const timezoneMap: { [key: string]: string } = {
      // Legacy to modern name mappings
      'Asia/Calcutta': 'Asia/Kolkata',
      'America/Godthab': 'America/Nuuk',
      'Europe/Kiev': 'Europe/Kyiv',
      'Asia/Katmandu': 'Asia/Kathmandu',
      'Asia/Rangoon': 'Asia/Yangon',
      'Asia/Saigon': 'Asia/Ho_Chi_Minh',

      // Timezones missing from your JSON
      'America/Cambridge_Bay': 'America/Edmonton',
      'America/Atikokan': 'America/Toronto',
      'America/Blanc-Sablon': 'America/Halifax',
      'America/Creston': 'America/Edmonton',
      'America/Ojinaga': 'America/Chihuahua',
      'America/Santa_Isabel': 'America/Tijuana',
      'America/Bahia_Banderas': 'America/Mexico_City',
      'America/Matamoros': 'America/Mexico_City',
      'America/Merida': 'America/Mexico_City',
      'America/Monterrey': 'America/Mexico_City',
      'America/Indiana/Indianapolis': 'America/New_York',
      'America/Indiana/Knox': 'America/Chicago',
      'America/Indiana/Marengo': 'America/New_York',
      'America/Indiana/Petersburg': 'America/New_York',
      'America/Indiana/Tell_City': 'America/Chicago',
      'America/Indiana/Vevay': 'America/New_York',
      'America/Indiana/Vincennes': 'America/New_York',
      'America/Indiana/Winamac': 'America/New_York',
      'America/Kentucky/Louisville': 'America/New_York',
      'America/Kentucky/Monticello': 'America/New_York',
      'America/North_Dakota/Beulah': 'America/Chicago',
      'America/North_Dakota/Center': 'America/Chicago',
      'America/North_Dakota/New_Salem': 'America/Chicago',  
    };
  
    return timezoneMap[timezone] || timezone;
  }
  

  handleButtonClick(action: 'CANCEL' | 'UPDATE'): void{
    const showTimezonePopup = JSON.parse(this.localStorageService.getLocalStorage('show_timezone_popup'));
    switch(action){
      case 'CANCEL':
        if(showTimezonePopup){
          this.localStorageService.setLocalStorage('show_timezone_popup', false);
        }
        this.dialogRef.close();
        break
      case 'UPDATE':
        const payload: any = {
          user: {
            timezone: this.systemTimeZone,
          }
        };
        this.profileSettingsService.updateUserPreferences(this.currentUser.user_uid, payload).subscribe({
          next: (response: any) => {
            this.profileSettingsService.updateUserDetailsInLocalStorage();
            this.localStorageService.setLocalStorage('has_timezone_difference', false);
            if(showTimezonePopup){
              this.localStorageService.setLocalStorage('show_timezone_popup', false);
            }
            this.toastService.success(response?.message);
            this.dialogRef.close(true);
          }
        });
        break;
    }
  }

}
